import React, { useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { deleteDictionaryItemAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import {
  IDictionaryNamedCohortFieldsFragment,
  INamedCohortEditMutation,
  INamedCohortEditMutationVariables,
  ManagedEntityType,
  PermissionKey,
  ScheduleType,
} from '../../../utils/apollo/graphqlSchema';
import {
  EDIT_NAMED_COHORT,
  GET_DICTIONARY_NAMED_COHORTS,
  NAMED_COHORT_ADD_CATEGORY,
  NAMED_COHORT_REMOVE_CATEGORY,
} from '../../../utils/apollo/cohorts';
import CohortDictionaryHeader from './CohortDictionaryHeader';
import { useAppSelector } from '../../../redux/hooks';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import useBootstrapCohortSchedules from './useBootstrapCohortSchedules';
import { CohortFilterType } from './types';
import Scheduler from '../../../components/Scheduler';
import DeleteCohortModal from '../../Dictionary/utils/DeleteCohortModal';
import DeleteScheduleModal from '../../Schedule/components/DeleteScheduleModal';
import TriggerScheduleModal from '../../Schedule/components/TriggerScheduleModal';
import { useScheduleModalHandlers } from '../../Schedule/utils';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { ModalSliceType } from '../../../redux/slices/modal';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { CategoryEntityType } from '../../../components/Categories/utils';
import { useDebounce } from '../../../utils/hooks';
import { IDictionaryStates } from '../../Dictionary/utils/types';
import { defaultDictionaryStates } from '../../Dictionary/utils/utils';
import { useImmer } from 'use-immer';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import useUser from '../../../utils/hooks/useUser';
import DictionaryNoResults from '../../Dictionary/DictionaryNoResults';
import CohortDictionaryTable from './CohortDictionaryTable';
import { RowSelectionState } from '@tanstack/react-table';
import useApplyTags from '../../Dictionary/useApplyTags';
import { useGlobalApplyTags } from '../../Dictionary/utils/useGlobalApplyTags';

const CohortDictionary = () => {
  const user = useUser();
  const dispatch = useDispatch();
  const client = useApolloClient();

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.CohortRead,
  });

  const {
    isDeleteScheduleModalOpen,
    isExecuteScheduleModalOpen,
    isEditScheduleModalOpen,
  } = useScheduleModalHandlers();

  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DeleteCohort, ''),
  );
  const [dictionaryStates, setDictionaryStates] = useImmer<IDictionaryStates>(
    defaultDictionaryStates,
  );

  const debouncedSearchQuery = useDebounce(dictionaryStates.searchTerm, 300);

  const [cohortFilterType, setCohortFilterType] = useState<CohortFilterType>(
    CohortFilterType.AllCohorts,
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { checkIsPermitted } = usePermission();

  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.CohortEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const loading = useBootstrapCohortSchedules({
    schemaId: selectedSchemaId,
    searchTerm: debouncedSearchQuery,
  });

  const namedCohorts: IDictionaryNamedCohortFieldsFragment[] = useAppSelector(
    (state) => state.dictionary.cohort,
  );
  const visibleCohorts: IDictionaryNamedCohortFieldsFragment[] = useMemo(() => {
    if (cohortFilterType === CohortFilterType.YourCohorts) {
      return namedCohorts.filter(
        (cohort) => cohort.version?.creator?.name === user.name,
      );
    }
    return namedCohorts;
  }, [namedCohorts, cohortFilterType, user.name]);

  const refreshTable = async () => {
    await client.refetchQueries({
      include: [GET_DICTIONARY_NAMED_COHORTS],
    });
  };
  const handleDisableCohort = (itemId: string) => {
    dispatch(
      deleteDictionaryItemAction({
        itemId,
        dictionaryType: DictionaryTypeEnum.DictionaryCohort,
      }),
    );
  };

  const handleEditMutationComplete = ({
    namedCohortEdit,
  }: INamedCohortEditMutation) => {
    const { entity, errors } = namedCohortEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `${entity.name} has been updated.`,
        type: 'info',
      });
    }
  };

  const [editNamedCohort] = useMutation<
    INamedCohortEditMutation,
    INamedCohortEditMutationVariables
  >(EDIT_NAMED_COHORT, {
    onCompleted: handleEditMutationComplete,
  });

  const onNameChange = async (
    cohort: IDictionaryNamedCohortFieldsFragment,
    newValue: string,
  ) => {
    const { data } = await editNamedCohort({
      variables: {
        input: {
          name: newValue.trim(),
          cohort: null, // kobol will not edit the cohort if null is passed in.
          comment: null,
          description: null,
          id: cohort.id,
          categories: null,
        },
      },
    });

    if (data) {
      // Refetch the cohorts query so the BuilderCohortSelector is up-to-date.
      await refreshTable();
    }
  };

  const onDescriptionChange = async (
    cohort: IDictionaryNamedCohortFieldsFragment,
    newValue: string,
  ) => {
    const { data } = await editNamedCohort({
      variables: {
        input: {
          name: null,
          cohort: null, // kobol will not edit the cohort if null is passed in.
          comment: null,
          description: newValue.trim(),
          id: cohort.id,
          categories: null,
        },
      },
    });

    if (data) {
      // Refetch the cohorts query so the BuilderCohortSelector is up-to-date.
      await refreshTable();
    }
  };

  const onSetFavorite = async () => {
    await refreshTable();
  };

  const getEntityId = (entity: IDictionaryNamedCohortFieldsFragment) =>
    entity.id;

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.Cohort,
    managedEntityType: ManagedEntityType.Cohort,
    addCategoryMutation: NAMED_COHORT_ADD_CATEGORY,
    removeCategoryMutation: NAMED_COHORT_REMOVE_CATEGORY,
    getEntityId,
    getEntityCategories: (entity: IDictionaryNamedCohortFieldsFragment) =>
      entity.categories,
    onSuccess: () => {
      refreshTable();
    },
  });

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: visibleCohorts,
    selectedSchemaId,
    handleApplyTags,
    getEntityId,
  });

  const selectedEntityCategories = visibleCohorts
    .filter((cohort) => rowSelection[getEntityId(cohort)])
    .map((cohort) => ({ categories: cohort.categories }));

  return (
    <>
      <CohortDictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={dictionaryStates.searchTerm}
        setSearchQuery={(value) =>
          setDictionaryStates((draft) => {
            draft.searchTerm = value;
          })
        }
        dictionaryItemCount={visibleCohorts.length}
        permittedSchemaIds={permittedSchemaIds}
        cohortFilterType={cohortFilterType}
        setCohortFilterType={setCohortFilterType}
        onApplyTags={handleGlobalApplyTags}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />
      {loading ? (
        <ContentLoader />
      ) : visibleCohorts.length === 0 ? (
        <DictionaryNoResults entityType={DictionaryTypeEnum.DictionaryCohort} />
      ) : (
        <CohortDictionaryTable
          namedCohorts={visibleCohorts}
          schemaId={selectedSchemaId}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onApplyTags={handleApplyTags}
          onChangeName={onNameChange}
          onChangeDescription={onDescriptionChange}
          onSetFavorite={onSetFavorite}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteCohortModal onSubmit={handleDisableCohort} />
      )}
      {isDeleteScheduleModalOpen && (
        <DeleteScheduleModal type={ScheduleType.BrazeCohortImport} />
      )}
      {isExecuteScheduleModalOpen && (
        <TriggerScheduleModal type={ScheduleType.BrazeCohortImport} />
      )}
      {isEditScheduleModalOpen && (
        <Scheduler jobType={ScheduleType.BrazeCohortImport} />
      )}
    </>
  );
};

export default CohortDictionary;
